import React, { forwardRef } from "react";
import { Box, type BoxProps } from "@mui/material";
import { tokens } from "@/theme/tokens";
import { cssTransition } from "@/components/Chat/motion";

export interface SurfaceProps extends BoxProps {
  /** Surface emphasis. Retained for call-site intent (`high` for primary
   *  floating chrome; `mid`/`low` for lighter overlays). Currently
   *  informational only — the flat treatment is the same across levels. */
  level?: "high" | "mid" | "low";
  /** When true the surface gains a hover lift + branded focus-within ring. */
  interactive?: boolean;
}

/**
 * Floating-chrome surface primitive for content that lifts ABOVE the page
 * (popovers, skeletons, proposal/refusal cards). A real surface tone (not the
 * page colour), a 1px ring visible in both themes, and the deepest step of
 * the shared elevation ramp.
 *
 * Resting content that sits flush on the page should use `Soft` instead.
 *
 * (Formerly a neumorphic extruded panel — see git history. Flattened
 * alongside `Soft`: the soft-UI shadow was the ONLY separation from the page
 * in dark mode, which made these read as invisible once the shadow was
 * toned down. A real background + ring fixes that regardless of shadow.)
 */
export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  { level: _level = "high", interactive = false, sx, children, ...rest },
  ref,
) {
  return (
    <Box
      ref={ref}
      sx={[
        (t) => {
          const dark = t.palette.mode === "dark";
          const ring = dark ? "rgba(255,255,255,0.09)" : "rgba(16,24,40,0.08)";
          return {
            position: "relative",
            borderRadius: `${tokens.radius.lg}px`,
            bgcolor: "background.paper",
            border: `1px solid ${ring}`,
            boxShadow: tokens.elevation[3],
            overflow: "hidden",
            ...(interactive && {
              transition: cssTransition.lift,
              "&:hover": { transform: "translateY(-2px)" },
              "&:focus-within": {
                boxShadow: dark ? tokens.focusRing.dark : tokens.focusRing.light,
              },
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
                "&:hover": { transform: "none" },
              },
            }),
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </Box>
  );
});
